import { DEFAULT_REQUEST_TIMEOUT_MS } from "./constants.js";
import { buildEnvelope, parseEnvelope, type OutboundEnvelope } from "./envelope.js";
import type {
  Bridge,
  Envelope,
  MessageContext,
  MessageHandler,
  MessageEventLike,
  RequestOptions,
  Transport,
  WindowLike,
} from "./types.js";

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  type: string;
}

interface ReadyResolver {
  resolve: () => void;
  reject: (reason: Error) => void;
}

/** Options passed by subclasses to the base constructor. */
export interface BaseBridgeOptions {
  /** The protocol version this side runs. */
  protocolVersion: number;
  /** The component id, or `null` until assigned during the handshake. */
  componentId: string | null;
  /** The low-level transport (web `WindowTransport` by default; native for content). */
  transport: Transport;
  /** Short prefix used to namespace generated request ids (e.g. "host"). */
  rolePrefix: string;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function isThenable(value: unknown): value is Promise<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { then?: unknown }).then === "function"
  );
}

function unrefTimer(timer: ReturnType<typeof setTimeout>): void {
  const maybe = timer as unknown as { unref?: () => void };
  if (typeof maybe.unref === "function") maybe.unref();
}

/**
 * Transport-agnostic core shared by the host and content bridges.
 *
 * It owns handler registration, request/response correlation, the ready lifecycle,
 * an outbound queue (so `send`/`request` before the channel is open are buffered),
 * and generic inbound dispatch. Subclasses implement the handshake and the
 * origin/source validation policy for their side.
 */
export abstract class BaseBridge implements Bridge {
  protected readonly transport: Transport;
  protected readonly protocolVersion: number;
  protected componentId: string | null;

  /** The window app frames are posted to (set once the channel opens). */
  protected peerWindow: WindowLike | null = null;
  /** The `targetOrigin` used for outbound frames (set once the channel opens). */
  protected peerOrigin: string | null = null;

  protected open = false;
  protected destroyed = false;
  protected handshakeError: Error | null = null;

  private readonly handlers = new Map<string, Set<MessageHandler>>();
  private readonly pending = new Map<string, PendingRequest>();
  private readonly readyListeners = new Set<() => void>();
  private readyResolvers: ReadyResolver[] = [];
  private outbox: OutboundEnvelope[] = [];

  private readonly idPrefix: string;
  private idCounter = 0;

  constructor(options: BaseBridgeOptions) {
    this.transport = options.transport;
    this.protocolVersion = options.protocolVersion;
    this.componentId = options.componentId;
    this.idPrefix = `${options.rolePrefix}-${Math.random().toString(36).slice(2, 8)}`;
    this.transport.subscribe(this.onFrame);
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  on(type: string, handler: MessageHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    return () => this.off(type, handler);
  }

  off(type: string, handler: MessageHandler): void {
    const set = this.handlers.get(type);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) this.handlers.delete(type);
  }

  send(type: string, payload?: unknown): void {
    this.postEnvelope({ kind: "message", type, payload });
  }

  request<TResponse = unknown>(
    type: string,
    payload?: unknown,
    options?: RequestOptions,
  ): Promise<TResponse> {
    const timeoutMs = options?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    return new Promise<TResponse>((resolve, reject) => {
      if (this.destroyed) {
        reject(new Error("Bridge has been destroyed"));
        return;
      }
      if (this.handshakeError) {
        reject(this.handshakeError);
        return;
      }
      const id = this.nextId();
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Request "${type}" timed out after ${timeoutMs}ms`));
      }, timeoutMs);
      unrefTimer(timer);
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
        type,
      });
      this.postEnvelope({ kind: "request", type, id, payload });
    });
  }

  ready(): boolean {
    return this.open;
  }

  onReady(callback: () => void): () => void {
    if (this.open) {
      queueMicrotask(() => {
        if (!this.destroyed) callback();
      });
      return () => {};
    }
    this.readyListeners.add(callback);
    return () => this.readyListeners.delete(callback);
  }

  whenReady(): Promise<void> {
    if (this.open) return Promise.resolve();
    if (this.handshakeError) return Promise.reject(this.handshakeError);
    if (this.destroyed) return Promise.reject(new Error("Bridge has been destroyed"));
    return new Promise<void>((resolve, reject) => {
      this.readyResolvers.push({ resolve, reject });
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.open = false;
    this.transport.destroy();
    const destroyError = new Error("Bridge has been destroyed");
    this.rejectAllPending(destroyError);
    this.rejectAllReadyResolvers(destroyError);
    this.handlers.clear();
    this.readyListeners.clear();
    this.outbox = [];
    this.peerWindow = null;
  }

  // ---------------------------------------------------------------------------
  // Subclass contract
  // ---------------------------------------------------------------------------

  /** Handle a validated inbound envelope (dispatch handshake + app frames). */
  protected abstract receive(envelope: Envelope, event: MessageEventLike): void;

  /** Is `source` the window this side expects frames to come from? */
  protected abstract isExpectedSource(source: unknown): boolean;

  /** Is `origin` acceptable for an inbound frame on this side? */
  protected abstract isAllowedOrigin(origin: string): boolean;

  // ---------------------------------------------------------------------------
  // Shared internals for subclasses
  // ---------------------------------------------------------------------------

  /** Build a full envelope from partial fields, applying this side's defaults. */
  protected buildEnvelope(fields: OutboundEnvelope): Envelope {
    return buildEnvelope(fields, {
      protocolVersion: this.protocolVersion,
      componentId: this.componentId ?? "",
    });
  }

  /**
   * Post a frame to the peer via the transport. On web the pinned peer
   * window/origin are used; on a trusted-peer (native) transport the target is
   * ignored and the frame is serialized to the native channel.
   */
  protected rawPost(envelope: Envelope): void {
    this.transport.post(envelope, {
      window: this.peerWindow,
      targetOrigin: this.peerOrigin ?? "",
    });
  }

  /**
   * Validate and dispatch an open-channel app frame
   * (`message` / `request` / `response` / `error`).
   */
  protected acceptOpenFrame(envelope: Envelope, event: MessageEventLike): void {
    if (!this.open) return;
    // On a trusted-peer transport (native) there is no window.parent / origin to
    // validate; the channel discriminator (already checked) + component_id are
    // still enforced exactly as on web.
    if (!this.transport.trustedPeer) {
      if (!this.isExpectedSource(event.source)) return;
      if (!this.isAllowedOrigin(event.origin)) return;
    }
    if (envelope.component_id !== this.componentId) return;
    this.dispatchEnvelope(envelope);
  }

  /** Mark the channel open, flush the outbox, and notify ready subscribers. */
  protected emitReady(): void {
    if (this.open || this.destroyed) return;
    this.open = true;
    this.flushOutbox();
    const resolvers = this.readyResolvers;
    this.readyResolvers = [];
    for (const resolver of resolvers) resolver.resolve();
    const listeners = [...this.readyListeners];
    this.readyListeners.clear();
    for (const listener of listeners) {
      try {
        listener();
      } catch {
        // ready listeners must not break the bridge
      }
    }
  }

  /** Abort the handshake: reject `whenReady()` and any queued requests. */
  protected failHandshake(error: Error): void {
    if (this.open || this.handshakeError || this.destroyed) return;
    this.handshakeError = error;
    this.rejectAllReadyResolvers(error);
    this.readyListeners.clear();
    this.rejectAllPending(error);
    this.outbox = [];
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private readonly onFrame = (event: MessageEventLike): void => {
    if (this.destroyed) return;
    const envelope = parseEnvelope(event.data);
    if (!envelope) return;
    this.receive(envelope, event);
  };

  private postEnvelope(fields: OutboundEnvelope): void {
    if (this.destroyed) return;
    // Queue the raw fields (not a built envelope) so that frames sent before the
    // handshake completes are stamped with the assigned `component_id` at flush
    // time, once it is known.
    if (!this.open) {
      this.outbox.push(fields);
      return;
    }
    this.rawPost(this.buildEnvelope(fields));
  }

  private flushOutbox(): void {
    const queued = this.outbox;
    this.outbox = [];
    for (const fields of queued) this.rawPost(this.buildEnvelope(fields));
  }

  private dispatchEnvelope(envelope: Envelope): void {
    switch (envelope.kind) {
      case "message":
        this.invokeMessageHandlers(envelope);
        break;
      case "request":
        void this.invokeRequestHandlers(envelope);
        break;
      case "response":
        this.resolvePending(envelope);
        break;
      case "error":
        this.rejectPending(envelope);
        break;
      default:
        // handshake kinds never reach here
        break;
    }
  }

  private invokeMessageHandlers(envelope: Envelope): void {
    const type = envelope.type;
    if (typeof type !== "string") return;
    const handlers = this.handlers.get(type);
    if (!handlers || handlers.size === 0) return;
    const context: MessageContext = { type, kind: "message" };
    for (const handler of [...handlers]) {
      try {
        const result = handler(envelope.payload, context);
        if (isThenable(result)) result.catch(() => {});
      } catch {
        // fire-and-forget: handler errors are ignored by design
      }
    }
  }

  private async invokeRequestHandlers(envelope: Envelope): Promise<void> {
    const type = envelope.type;
    const id = envelope.id;
    if (typeof type !== "string" || typeof id !== "string") return;
    const handlers = this.handlers.get(type);
    if (!handlers || handlers.size === 0) {
      this.postEnvelope({
        kind: "error",
        type,
        id,
        error: `No handler registered for request type "${type}"`,
      });
      return;
    }
    const context: MessageContext = { type, kind: "request", id };
    try {
      const results = await Promise.all(
        [...handlers].map((handler) => handler(envelope.payload, context)),
      );
      const payload = results.find((result) => result !== undefined);
      this.postEnvelope({ kind: "response", type, id, payload });
    } catch (error) {
      this.postEnvelope({ kind: "error", type, id, error: toErrorMessage(error) });
    }
  }

  private resolvePending(envelope: Envelope): void {
    const id = envelope.id;
    if (typeof id !== "string") return;
    const pending = this.pending.get(id);
    if (!pending) return;
    this.pending.delete(id);
    clearTimeout(pending.timer);
    pending.resolve(envelope.payload);
  }

  private rejectPending(envelope: Envelope): void {
    const id = envelope.id;
    if (typeof id !== "string") return;
    const pending = this.pending.get(id);
    if (!pending) return;
    this.pending.delete(id);
    clearTimeout(pending.timer);
    pending.reject(new Error(envelope.error ?? `Request "${pending.type}" failed`));
  }

  private rejectAllPending(error: Error): void {
    const pendings = [...this.pending.values()];
    this.pending.clear();
    for (const pending of pendings) {
      clearTimeout(pending.timer);
      pending.reject(error);
    }
  }

  private rejectAllReadyResolvers(error: Error): void {
    const resolvers = this.readyResolvers;
    this.readyResolvers = [];
    for (const resolver of resolvers) resolver.reject(error);
  }

  private nextId(): string {
    this.idCounter += 1;
    return `${this.idPrefix}-${this.idCounter}`;
  }
}
